"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";

import { TextField } from "@/components/shared/text-field";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup } from "@/components/ui/field";
import { routes } from "@/config/routes";
import { loginAction } from "@/features/auth/actions";
import { FormAlert } from "@/features/auth/components/form-alert";
import { FormHeader } from "@/features/auth/components/form-header";
import { MfaCodeForm } from "@/features/auth/components/mfa-code-form";
import { applyServerErrors } from "@/features/auth/form-errors";
import { loginSchema, type LoginInput } from "@/features/auth/schemas";

/** Password, then the code step when the account has MFA (membership-and-mfa.md rule 16). */
export function LoginForm() {
  const t = useTranslations("auth");
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function handleSubmit(values: LoginInput) {
    const result = await loginAction(values, next); // redirects when no code is needed
    if (result.ok) setChallengeToken(result.data.challengeToken);
    else applyServerErrors(form, ["email", "password"], result.error, result.fieldErrors);
  }

  if (challengeToken) {
    return (
      <MfaCodeForm
        challengeToken={challengeToken}
        next={next}
        onRestart={(message) => {
          setChallengeToken(null);
          form.resetField("password");
          if (message) form.setError("root", { message });
        }}
      />
    );
  }

  const { isSubmitting, errors } = form.formState;

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} noValidate className="flex flex-col gap-6">
      <FieldGroup>
        <FormHeader title={t("login.title")} description={t("login.description")} />
        {searchParams.get("reset") === "1" && !errors.root && (
          <FormAlert variant="default" message={t("login.passwordUpdated")} />
        )}
        <FormAlert message={errors.root?.message} />

        <TextField
          control={form.control}
          name="email"
          label={t("email")}
          type="email"
          autoComplete="email"
        />
        <TextField
          control={form.control}
          name="password"
          label={t("password")}
          type="password"
          autoComplete="current-password"
          labelAction={
            <Link
              href={routes.forgotPassword()}
              className="text-sm underline-offset-4 hover:underline"
            >
              {t("login.forgotPassword")}
            </Link>
          }
        />

        <Field>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("login.submitting") : t("login.submit")}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
